import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { borderRadius } from '../../tokens/spacing';
import { colors } from '../../tokens/colors';
import { transitions, durations } from '../../utils/motion';

/* ── Types ── */

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  iconOnly?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  children?: ReactNode;
}

/* ── Style maps ── */

const variantTokens: Record<ButtonVariant, {
  '--btn-bg': string;
  '--btn-color': string;
  '--btn-border': string;
  '--btn-hover-bg': string;
  '--btn-hover-border': string;
  '--btn-active-bg': string;
  '--btn-active-border': string;
  '--btn-glow': string;
  '--btn-shadow': string;
  '--btn-hover-shadow': string;
  '--btn-active-shadow': string;
}> = {
  primary: {
    '--btn-bg': colors.primary[500],
    '--btn-color': '#ffffff',
    '--btn-border': 'transparent',
    '--btn-hover-bg': colors.primary[600],
    '--btn-hover-border': colors.primary[400],
    '--btn-active-bg': colors.primary[700],
    '--btn-active-border': colors.primary[500],
    '--btn-glow': `0 0 0 2px ${colors.primary[100]}`,
    '--btn-shadow': 'none',
    '--btn-hover-shadow': `0 0 20px ${colors.primary[300]}44, 0 0 40px ${colors.primary[300]}22`,
    '--btn-active-shadow': `0 0 10px ${colors.primary[300]}33`,
  },
  secondary: {
    '--btn-bg': '#ffffff',
    '--btn-color': colors.text.primary,
    '--btn-border': colors.border.default,
    '--btn-hover-bg': colors.neutral[50],
    '--btn-hover-border': colors.primary[500],
    '--btn-active-bg': colors.neutral[100],
    '--btn-active-border': colors.border.focus,
    '--btn-glow': `0 0 0 2px ${colors.primary[100]}`,
    '--btn-shadow': 'none',
    '--btn-hover-shadow': `0 0 16px ${colors.primary[300]}22`,
    '--btn-active-shadow': 'none',
  },
  outline: {
    '--btn-bg': 'transparent',
    '--btn-color': colors.primary[500],
    '--btn-border': colors.primary[500],
    '--btn-hover-bg': colors.primary[50],
    '--btn-hover-border': colors.primary[600],
    '--btn-active-bg': colors.primary[100],
    '--btn-active-border': colors.primary[600],
    '--btn-glow': `0 0 0 2px ${colors.primary[100]}`,
    '--btn-shadow': 'none',
    '--btn-hover-shadow': `0 0 12px ${colors.primary[300]}33`,
    '--btn-active-shadow': 'none',
  },
  ghost: {
    '--btn-bg': 'transparent',
    '--btn-color': colors.text.secondary,
    '--btn-border': 'transparent',
    '--btn-hover-bg': colors.neutral[100],
    '--btn-hover-border': 'transparent',
    '--btn-active-bg': colors.neutral[200],
    '--btn-active-border': 'transparent',
    '--btn-glow': `0 0 0 2px ${colors.neutral[200]}`,
    '--btn-shadow': 'none',
    '--btn-hover-shadow': 'none',
    '--btn-active-shadow': 'none',
  },
  danger: {
    '--btn-bg': colors.error[500],
    '--btn-color': '#ffffff',
    '--btn-border': 'transparent',
    '--btn-hover-bg': colors.error[600],
    '--btn-hover-border': colors.error[400],
    '--btn-active-bg': colors.error[700],
    '--btn-active-border': colors.error[500],
    '--btn-glow': `0 0 0 2px ${colors.error[100]}`,
    '--btn-shadow': 'none',
    '--btn-hover-shadow': `0 0 20px ${colors.error[300]}44`,
    '--btn-active-shadow': `0 0 10px ${colors.error[300]}33`,
  },
};

const sizeTokens: Record<ButtonSize, { padding: string; fontSize: string; gap: string; minHeight: string; borderRadius: string }> = {
  sm: { padding: '0.375rem 0.75rem', fontSize: '0.8125rem', gap: '0.375rem', minHeight: '32px', borderRadius: borderRadius.sm },
  md: { padding: '0.5rem 1rem', fontSize: '0.875rem', gap: '0.5rem', minHeight: '40px', borderRadius: borderRadius.sm },
  lg: { padding: '0.625rem 1.25rem', fontSize: '1rem', gap: '0.5rem', minHeight: '48px', borderRadius: '8px' },
};

/* ── Component ── */

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      iconOnly = false,
      iconLeft,
      iconRight,
      disabled,
      children,
      className,
      style,
      ...props
    },
    ref,
  ): JSX.Element => {
    const isDisabled = disabled || loading;
    const vToken = variantTokens[variant];
    const sToken = sizeTokens[size];

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading}
        aria-disabled={isDisabled}
        role="button"
        className={cn(
          'ds-btn',
          `ds-btn--${variant}`,
          `ds-btn--${size}`,
          fullWidth && 'ds-btn--full',
          iconOnly && 'ds-btn--icon-only',
          'ds-btn--enhanced',
          className,
        )}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          fontFamily: 'inherit',
          userSelect: 'none',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.5 : 1,
          width: fullWidth ? '100%' : iconOnly ? sToken.minHeight : undefined,
          aspectRatio: iconOnly ? '1' : undefined,
          borderRadius: iconOnly ? '50%' : sToken.borderRadius,
          padding: iconOnly ? '0' : sToken.padding,
          fontSize: sToken.fontSize,
          gap: sToken.gap,
          minHeight: sToken.minHeight,
          outline: 'none',
          ...vToken,
          ...style,
        }}
        {...props}
      >
        {loading && (
          <span
            className="ds-btn__spinner"
            style={{
              width: size === 'sm' ? '12px' : '14px',
              height: size === 'sm' ? '12px' : '14px',
              border: '2px solid currentColor',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: `ds-btn-spin ${durations.spin} linear infinite`,
              display: 'inline-block',
              flexShrink: 0,
            }}
          />
        )}
        {!loading && iconLeft && (
          <span className="ds-btn__icon ds-btn__icon--left" style={{ display: 'inline-flex', flexShrink: 0 }}>
            {iconLeft}
          </span>
        )}
        {children && !iconOnly && <span className="ds-btn__text">{children}</span>}
        {!loading && iconRight && (
          <span className="ds-btn__icon ds-btn__icon--right" style={{ display: 'inline-flex', flexShrink: 0 }}>
            {iconRight}
          </span>
        )}

        <style>{`
          .ds-btn--enhanced {
            transition:
              background ${transitions.fast},
              border-color ${transitions.fast},
              box-shadow ${transitions.fast},
              transform ${transitions.fast},
              opacity ${transitions.fast};
            background: var(--btn-bg);
            color: var(--btn-color);
            border: 1px solid var(--btn-border);
            box-shadow: var(--btn-shadow);
          }
          .ds-btn--enhanced:focus-visible {
            box-shadow: var(--btn-glow) !important;
          }
          .ds-btn--enhanced:hover:not(:disabled):not([aria-busy="true"]) {
            background: var(--btn-hover-bg);
            border-color: var(--btn-hover-border);
            box-shadow: var(--btn-hover-shadow);
            transform: translateY(-1px);
          }
          .ds-btn--enhanced:active:not(:disabled):not([aria-busy="true"]) {
            background: var(--btn-active-bg);
            border-color: var(--btn-active-border);
            box-shadow: var(--btn-active-shadow);
            transform: translateY(0);
          }
          /*
            Muitas chamadas passam o icone como FILHO do Button, em vez de usar
            a prop icon. Nesse caso o icone cai dentro de .ds-btn__text, e um
            svg ali e block-level: o icone ia para cima e o rotulo para baixo,
            virando um botao de duas linhas. Foi assim que o "Filtrar" da tela
            de Auditoria renderizou — visto na captura, nao no codigo.
          */
          .ds-btn__text {
            display: inline-flex;
            align-items: center;
            gap: inherit;
          }
          .ds-btn__text > svg {
            flex-shrink: 0;
          }
          .ds-btn--icon-only {
            padding: 0 !important;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          .ds-btn--icon-only .ds-btn__icon {
            margin: 0;
          }
          @keyframes ds-btn-spin { to { transform: rotate(360deg); } }
          @media (prefers-reduced-motion: reduce), [data-reduced-motion="true"] {
            .ds-btn--enhanced { transition: none !important; }
            .ds-btn--enhanced:hover:not(:disabled) { transform: none !important; }
            .ds-btn__spinner { animation: none !important; opacity: 0.6; }
          }
        `}</style>
      </button>
    );
  },
);

Button.displayName = 'Button';
